import {
  Stack,
  Button,
  Group,
  Text,
  TextInput,
  Textarea,
  NumberInput,
  Loader,
  Alert,
  Table,
  Checkbox,
  ScrollArea,
  ActionIcon,
  Box,
  Autocomplete,
  Collapse,
  Paper,
  ThemeIcon,
  Pill,
  Progress,
} from "@mantine/core";
import { ResponsiveDrawer } from "../UI/ResponsiveDrawer";
import { IconAlertCircle, IconX, IconPlus, IconStopwatch, IconRefresh, IconSparkles, IconPhoto, IconBaguette, IconEdit, IconMicrophone, IconVideo, IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { DrawerHeader } from "../UI/DrawerHeader";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useQuickBites } from "../../hooks/useQuickBites";
import { useSignedUpload } from "../../hooks/useSignedUpload";
import { PantryItem } from "../../types/pantry";
import { Recipe } from "../../types/mealPlan";
import { searchIngredients } from "../../data/ingredientDatabase";
import { QuickBiteRecipeCard } from "./QuickBiteRecipeCard";
import { PantryImageUpload } from "../Pantry/PantryImageUpload";
import { PantryVideoRecorder } from "../Pantry/PantryVideoRecorder";
import { VoiceRecorder } from "../UI/VoiceRecorder";
import { HouseholdSegment } from "../../utils/householdSegment";

type QuickBiteDrawerProps = {
  opened: boolean;
  onClose: () => void;
  onRecipeGenerated: (recipe: Recipe | Recipe[]) => void;
  userDietaryRestrictions?: string[] | null;
  userHouseholdSegment?: HouseholdSegment;
};

export const QuickBiteDrawer = ({
  opened,
  onClose,
  onRecipeGenerated,
  userDietaryRestrictions,
  userHouseholdSegment,
}: QuickBiteDrawerProps) => {
  const { user } = useAuth();
  const { generateQuickBites, saveQuickBites, loading } = useQuickBites();
  const { upload: uploadVideo, progress: videoUploadProgress, uploading: videoUploading, reset: resetVideoUpload } = useSignedUpload();

  // CSS for forcing pointer cursor on all child elements
  const cardWrapperStyle = `
    .quick-bite-card-wrapper * {
      cursor: pointer !important;
    }
    .quick-bite-card-wrapper.loading * {
      cursor: not-allowed !important;
    }
  `;

  // Generated recipes state (now 3 recipes)
  const [generatedRecipes, setGeneratedRecipes] = useState<Recipe[]>([]);
  const [selectedRecipeIds, setSelectedRecipeIds] = useState<Set<string>>(
    new Set()
  );
  const [showCustomizeInput, setShowCustomizeInput] = useState(false);
  const [customizeInstructions, setCustomizeInstructions] = useState("");
  const [saving, setSaving] = useState(false);

  // Pantry items
  const [pantryItems, setPantryItems] = useState<PantryItem[]>([]);
  const [loadingPantry, setLoadingPantry] = useState(false);
  const [selectedPantryIds, setSelectedPantryIds] = useState<Set<string>>(
    new Set()
  );
  const [pantrySearchTerm, setPantrySearchTerm] = useState("");

  // Custom ingredients
  const [customIngredients, setCustomIngredients] = useState<string[]>([]);
  const [newIngredient, setNewIngredient] = useState("");
  const ingredientSuggestions = useMemo(
    () => newIngredient.length >= 2 ? searchIngredients(newIngredient, 8).map(ing => ing.displayName ?? ing.name) : [],
    [newIngredient]
  );

  // Images
  const [images, setImages] = useState<File[]>([]);

  // Voice recording
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);

  // Video
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoFileId, setVideoFileId] = useState<string | null>(null);

  // Expanded input sections
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Advanced options
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // Form fields
  const [servings, setServings] = useState<number>(4);
  const [customInstructions, setCustomInstructions] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Fetch family profile to calculate default servings
  useEffect(() => {
    const fetchFamilyProfile = async () => {
      if (!user || !opened) return;

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/user/profile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const familyProfile = data.user?.familyProfile;
          if (familyProfile) {
            const adults = familyProfile.adults || 2;
            const kids = familyProfile.kids || 0;
            setServings(adults + kids);
          }
        }
      } catch (error) {
        console.error("Error fetching family profile:", error);
      }
    };

    fetchFamilyProfile();
  }, [user, opened]);

  // Fetch pantry items
  useEffect(() => {
    const fetchPantry = async () => {
      if (!user || !opened) return;

      setLoadingPantry(true);
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/pantry`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.ok) {
          const data = await response.json();
          // Filter out items that need review
          const reviewedItems = (data.items || []).filter(
            (item: PantryItem) => !item.needsReview
          );
          setPantryItems(reviewedItems);
        }
      } catch (error) {
        console.error("Error fetching pantry:", error);
      } finally {
        setLoadingPantry(false);
      }
    };

    fetchPantry();
  }, [user, opened]);

  // Reset form when drawer closes
  useEffect(() => {
    if (!opened) {
      setGeneratedRecipes([]);
      setSelectedRecipeIds(new Set());
      setShowCustomizeInput(false);
      setCustomizeInstructions("");
      setSelectedPantryIds(new Set());
      setCustomIngredients([]);
      setNewIngredient("");
      setImages([]);
      setAudioBlob(null);
      setVideoBlob(null);
      setVideoFileId(null);
      resetVideoUpload();
      setExpandedSection(null);
      setShowAdvancedOptions(false);
      // Don't reset servings here - will be set by family profile fetch when reopened
      setCustomInstructions("");
      setError(null);
      setPantrySearchTerm("");
    }
  }, [opened, resetVideoUpload]);

  const handleTogglePantryItem = (id: string) => {
    const newSelected = new Set(selectedPantryIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedPantryIds(newSelected);
  };

  const handleAddCustomIngredient = () => {
    const trimmed = newIngredient.trim();
    if (trimmed && !customIngredients.includes(trimmed)) {
      setCustomIngredients([...customIngredients, trimmed]);
      setNewIngredient("");
    }
  };

  const handleRemoveCustomIngredient = (index: number) => {
    setCustomIngredients(customIngredients.filter((_, i) => i !== index));
  };

  const handleImagesSelected = (selectedImages: File[]) => {
    setImages(selectedImages);
    // Video and images are mutually exclusive
    if (selectedImages.length > 0) {
      setVideoBlob(null);
      resetVideoUpload();
    }
  };

  const handleImagesCleared = () => {
    setImages([]);
  };

  const handleVideoReady = useCallback((video: Blob) => {
    setVideoBlob(video);
    // Video and images are mutually exclusive
    setImages([]);
  }, []);

  const handleVideoCleared = useCallback(() => {
    setVideoBlob(null);
    setVideoFileId(null);
    resetVideoUpload();
  }, [resetVideoUpload]);

  const handleGenerate = async () => {
    setError(null);

    const hasAudio = !!audioBlob;
    const hasImages = images.length > 0;
    const hasVideo = !!videoBlob;
    const hasPantryItems = selectedPantryIds.size > 0;
    const hasCustomIngredients = customIngredients.length > 0;
    const totalIngredients = selectedPantryIds.size + customIngredients.length;

    // Validation: need at least one input source
    if (!hasAudio && !hasImages && !hasVideo && !hasPantryItems && !hasCustomIngredients) {
      setError("Please provide at least one input: video, voice recording, image, pantry item, or custom ingredient");
      return;
    }

    // If no audio, no images, and no video, need at least 2 ingredients
    if (!hasAudio && !hasImages && !hasVideo && totalIngredients < 2) {
      setError("Please provide at least 2 ingredients, or use a photo, video, or voice recording");
      return;
    }

    if (totalIngredients > 15) {
      setError("Maximum 15 ingredients allowed");
      return;
    }

    try {
      // Upload video via signed URL if present (reuse existing fileId if available)
      let currentVideoFileId: string | undefined = videoFileId || undefined;
      if (hasVideo && videoBlob && !currentVideoFileId) {
        const contentType = videoBlob.type || 'video/webm';
        currentVideoFileId = await uploadVideo(videoBlob, 'recording.webm', contentType);
        setVideoFileId(currentVideoFileId);
      }

      const recipes = await generateQuickBites({
        pantryItemIds: hasPantryItems ? Array.from(selectedPantryIds) : undefined,
        customIngredients: hasCustomIngredients ? customIngredients : undefined,
        servings,
        customInstructions: customInstructions.trim() || undefined,
        images: hasImages ? images : undefined,
        audio: hasAudio ? audioBlob : undefined,
        videoFileId: currentVideoFileId,
      });

      setGeneratedRecipes(recipes);
      setSelectedRecipeIds(new Set());
    } catch (error: any) {
      setError(error.message || "Failed to generate quick bites");
    }
  };

  const handleRequestNewRecipe = async () => {
    setError(null);
    setShowCustomizeInput(false);

    try {
      const recipes = await generateQuickBites({
        pantryItemIds: Array.from(selectedPantryIds),
        customIngredients: customIngredients.length > 0 ? customIngredients : undefined,
        servings,
        customInstructions: undefined,
        images: images.length > 0 ? images : undefined,
        videoFileId: videoFileId || undefined,
      });

      setGeneratedRecipes(recipes);
      setSelectedRecipeIds(new Set());
    } catch (error: any) {
      setError(error.message || "Failed to generate quick bites");
    }
  };

  const handleCustomizeWithInstructions = () => {
    setShowCustomizeInput(true);
    setCustomizeInstructions("");
    setError(null);
  };

  const handleGenerateWithCustomInstructions = async () => {
    setError(null);

    if (!customizeInstructions.trim()) {
      setError("Please enter custom instructions");
      return;
    }

    try {
      const recipes = await generateQuickBites({
        pantryItemIds: Array.from(selectedPantryIds),
        customIngredients: customIngredients.length > 0 ? customIngredients : undefined,
        servings,
        customInstructions: customizeInstructions.trim(),
        images: images.length > 0 ? images : undefined,
        videoFileId: videoFileId || undefined,
      });

      setGeneratedRecipes(recipes);
      setSelectedRecipeIds(new Set());
      setShowCustomizeInput(false);
      setCustomizeInstructions("");
    } catch (error: any) {
      setError(error.message || "Failed to generate quick bites");
    }
  };

  const handleSaveSelected = async () => {
    setError(null);

    if (selectedRecipeIds.size === 0) {
      setError("Please select at least one recipe to save");
      return;
    }

    setSaving(true);
    try {
      const savedRecipes = await saveQuickBites(Array.from(selectedRecipeIds));

      // Notify parent about saved recipes
      onRecipeGenerated(savedRecipes);

      // Close drawer on success
      onClose();
    } catch (error: any) {
      setError(error.message || "Failed to save recipes");
    } finally {
      setSaving(false);
    }
  };

  // Filter pantry items based on search
  const filteredPantryItems = pantryItems.filter((item) =>
    item.displayName.toLowerCase().includes(pantrySearchTerm.toLowerCase())
  );

  const totalSelected = selectedPantryIds.size + customIngredients.length;
  const hasImages = images.length > 0;
  const hasAudio = audioBlob !== null;
  const hasVideo = videoBlob !== null;
  const hasEnoughInput = hasAudio || hasImages || hasVideo || totalSelected >= 2;
  const isUploading = videoUploading;

  return (
    <>
      <style>{cardWrapperStyle}</style>
      <ResponsiveDrawer
        opened={opened}
        onClose={loading || generatedRecipes.length > 0 ? () => {} : onClose}
        closeOnClickOutside={!loading && generatedRecipes.length === 0}
        closeOnEscape={!loading && generatedRecipes.length === 0}
        withCloseButton={!loading && generatedRecipes.length === 0}
          title={
        <DrawerHeader
          icon={<IconStopwatch size={18} />}
          title="Quick bites"
        />
      }
      size="md"
    >
      {generatedRecipes.length > 0 ? (
        // Show generated recipe cards (3 recipes)
        <Stack gap="md" style={{ position: 'relative' }}>
          {loading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 'var(--mantine-radius-md)',
            }}>
              <Loader size="lg" />
            </div>
          )}

          <Text size="sm" c="dimmed">
            Select recipes to save
          </Text>

          {/* Secondary actions at top - hide when customizing */}
          {!showCustomizeInput && (
            <Group grow>
              <Button
                variant="light"
                leftSection={<IconRefresh size={18} />}
                onClick={handleRequestNewRecipe}
                disabled={loading}
              >
                New Recipes
              </Button>
              <Button
                variant="light"
                leftSection={<IconSparkles size={18} />}
                onClick={handleCustomizeWithInstructions}
                disabled={loading}
              >
                Customize
              </Button>
            </Group>
          )}

          {/* Customize Input - show at top when active */}
          {showCustomizeInput && (
            <Stack gap="xs">
              <Textarea
                placeholder='Enter custom instructions (e.g. "make it spicy", "add more garlic", "use less salt")'
                value={customizeInstructions}
                onChange={(e) => setCustomizeInstructions(e.target.value)}
                autosize
                minRows={3}
                maxRows={5}
                maxLength={500}
              />
              <Group justify="flex-end">
                <Button
                  variant="subtle"
                  onClick={() => {
                    setShowCustomizeInput(false);
                    setCustomizeInstructions("");
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerateWithCustomInstructions}
                  disabled={!customizeInstructions.trim() || loading}
                  loading={loading}
                >
                  Generate
                </Button>
              </Group>
            </Stack>
          )}

          {generatedRecipes.map((recipe) => {
            const isSelected = selectedRecipeIds.has(recipe.id);
            const toggleSelection = () => {
              if (loading) return;
              const newSelected = new Set(selectedRecipeIds);
              if (newSelected.has(recipe.id)) {
                newSelected.delete(recipe.id);
              } else {
                newSelected.add(recipe.id);
              }
              setSelectedRecipeIds(newSelected);
            };

            return (
              <Box
                key={recipe.id}
                onClick={toggleSelection}
                style={{
                  position: 'relative',
                  border: isSelected
                    ? '2px solid var(--mantine-color-blue-6)'
                    : '2px solid transparent',
                  borderRadius: 'var(--mantine-radius-md)',
                  padding: '8px',
                  transition: 'border-color 0.2s',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                }}
                className={loading ? "quick-bite-card-wrapper loading" : "quick-bite-card-wrapper"}
              >
                <QuickBiteRecipeCard recipe={recipe} disableHover={true} isSelected={isSelected} userDietaryRestrictions={userDietaryRestrictions} userHouseholdSegment={userHouseholdSegment} onRecipeClick={toggleSelection} radius="lg" />
              </Box>
            );
          })}

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" radius="lg">
              {error}
            </Alert>
          )}

          {/* Action buttons at bottom */}
          <Group grow>
            <Button
              variant="subtle"
              onClick={onClose}
              disabled={loading || saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSelected}
              disabled={loading || saving || selectedRecipeIds.size === 0}
              loading={saving}
            >
              Save ({selectedRecipeIds.size})
            </Button>
          </Group>
        </Stack>
      ) : (
        // Show ingredient selection form
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Create a fast recipe with what you have on hand
          </Text>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" radius="lg">
              {error}
            </Alert>
          )}

          {/* Input Methods */}
          <Text size="sm" fw={500}>
            What do you have?
          </Text>

          <Stack gap="xs">
            {/* Photos Section */}
            <Paper withBorder radius="lg" style={hasVideo ? { opacity: 0.5 } : undefined}>
              <Box
                p="sm"
                style={{ cursor: loading || hasVideo ? "not-allowed" : "pointer" }}
                onClick={() => !loading && !hasVideo && setExpandedSection(expandedSection === "photos" ? null : "photos")}
              >
                <Group justify="space-between">
                  <Group gap="sm">
                    <ThemeIcon variant="light" size="sm" color={images.length > 0 ? "orange" : "gray"}>
                      <IconPhoto size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={500}>Snap a photo</Text>
                    {images.length > 0 && (
                      <Pill size="sm">{images.length} {images.length === 1 ? "photo" : "photos"}</Pill>
                    )}
                    {hasVideo && (
                      <Text size="xs" c="dimmed">disabled when video is attached</Text>
                    )}
                  </Group>
                  {expandedSection === "photos" ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </Group>
              </Box>
              <Collapse in={expandedSection === "photos" && !hasVideo}>
                <Box px="sm" pb="sm">
                  <PantryImageUpload
                    onImagesSelected={handleImagesSelected}
                    onImagesCleared={handleImagesCleared}
                    disabled={loading}
                    showTips={false}
                    maxImages={5}
                  />
                </Box>
              </Collapse>
            </Paper>

            {/* Video Section */}
            <Paper withBorder radius="lg" style={hasImages ? { opacity: 0.5 } : undefined}>
              <Box
                p="sm"
                style={{ cursor: loading || hasImages ? "not-allowed" : "pointer" }}
                onClick={() => !loading && !hasImages && setExpandedSection(expandedSection === "video" ? null : "video")}
              >
                <Group justify="space-between">
                  <Group gap="sm">
                    <ThemeIcon variant="light" size="sm" color={hasVideo ? "orange" : "gray"}>
                      <IconVideo size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={500}>Record a video</Text>
                    {hasVideo && (
                      <Pill size="sm">video attached</Pill>
                    )}
                    {hasImages && (
                      <Text size="xs" c="dimmed">disabled when photos are attached</Text>
                    )}
                  </Group>
                  {expandedSection === "video" ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </Group>
              </Box>
              <Collapse in={expandedSection === "video" && !hasImages}>
                <Box px="sm" pb="sm">
                  <PantryVideoRecorder
                    onVideoReady={handleVideoReady}
                    onVideoCleared={handleVideoCleared}
                    disabled={loading || isUploading}
                  />
                </Box>
              </Collapse>
            </Paper>

            {/* Voice Section */}
            <Paper withBorder radius="lg">
              <Box
                p="sm"
                style={{ cursor: loading ? "not-allowed" : "pointer" }}
                onClick={() => !loading && setExpandedSection(expandedSection === "voice" ? null : "voice")}
              >
                <Group justify="space-between">
                  <Group gap="sm">
                    <ThemeIcon variant="light" size="sm" color={audioBlob ? "orange" : "gray"}>
                      <IconMicrophone size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={500}>Describe what you have</Text>
                    {audioBlob && (
                      <Pill size="sm">recorded</Pill>
                    )}
                  </Group>
                  {expandedSection === "voice" ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </Group>
              </Box>
              <Collapse in={expandedSection === "voice"}>
                <Box px="sm" pb="sm">
                  <VoiceRecorder
                    onRecordingComplete={(blob) => setAudioBlob(blob)}
                    onRecordingCleared={() => setAudioBlob(null)}
                    disabled={loading}
                    idleHint="Describe what ingredients you have (max 5 minutes)"
                  />
                </Box>
              </Collapse>
            </Paper>

            {/* Add ingredients Section */}
            <Paper withBorder radius="lg">
              <Box
                p="sm"
                style={{ cursor: loading ? "not-allowed" : "pointer" }}
                onClick={() => !loading && setExpandedSection(expandedSection === "manual" ? null : "manual")}
              >
                <Group justify="space-between">
                  <Group gap="sm">
                    <ThemeIcon variant="light" size="sm" color={customIngredients.length > 0 ? "orange" : "gray"}>
                      <IconEdit size={14} />
                    </ThemeIcon>
                    <Text size="sm" fw={500}>Add ingredients</Text>
                    {customIngredients.length > 0 && (
                      <Pill size="sm">{customIngredients.length} {customIngredients.length === 1 ? "ingredient" : "ingredients"}</Pill>
                    )}
                  </Group>
                  {expandedSection === "manual" ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </Group>
              </Box>
              <Collapse in={expandedSection === "manual"}>
                <Box px="sm" pb="sm">
                  <Group gap="xs" mb="xs">
                    <Autocomplete
                      placeholder="Enter ingredient..."
                      value={newIngredient}
                      onChange={(value) => setNewIngredient(value)}
                      onOptionSubmit={(value) => {
                        const trimmedValue = value.trim();
                        if (trimmedValue && !customIngredients.includes(trimmedValue)) {
                          setCustomIngredients([...customIngredients, trimmedValue]);
                          setTimeout(() => setNewIngredient(""), 0);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddCustomIngredient();
                        }
                      }}
                      data={ingredientSuggestions}
                      limit={8}
                      style={{ flex: 1 }}
                      disabled={loading}
                      comboboxProps={{ withinPortal: false }}
                    />
                    <ActionIcon
                      variant="filled"
                      size="lg"
                      onClick={handleAddCustomIngredient}
                      disabled={!newIngredient.trim() || loading}
                    >
                      <IconPlus size={16} />
                    </ActionIcon>
                  </Group>

                  {customIngredients.length > 0 && (
                    <Stack gap={0}>
                      {customIngredients.map((ingredient, index) => (
                        <Box key={index} p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: '4px' }}>
                          <Group gap="xs" align="center">
                            <Text style={{ flex: 1 }} size="sm">{ingredient}</Text>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="sm"
                              onClick={() => handleRemoveCustomIngredient(index)}
                              disabled={loading}
                            >
                              <IconX size={14} />
                            </ActionIcon>
                          </Group>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Collapse>
            </Paper>

            {/* Pantry Section */}
            <Collapse in={pantryItems.length > 0}>
              <Paper withBorder radius="lg">
                <Box
                  p="sm"
                  style={{ cursor: loading ? "not-allowed" : "pointer" }}
                  onClick={() => !loading && setExpandedSection(expandedSection === "pantry" ? null : "pantry")}
                >
                  <Group justify="space-between">
                    <Group gap="sm">
                      <ThemeIcon variant="light" size="sm" color={selectedPantryIds.size > 0 ? "orange" : "gray"}>
                        <IconBaguette size={14} />
                      </ThemeIcon>
                      <Text size="sm" fw={500}>Pick from pantry</Text>
                      {selectedPantryIds.size > 0 && (
                        <Pill size="sm">{selectedPantryIds.size} {selectedPantryIds.size === 1 ? "item" : "items"}</Pill>
                      )}
                    </Group>
                    {expandedSection === "pantry" ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                  </Group>
                </Box>
                <Collapse in={expandedSection === "pantry"}>
                  <Box px="sm" pb="sm">
                    <TextInput
                      placeholder="Search pantry..."
                      value={pantrySearchTerm}
                      onChange={(e) => setPantrySearchTerm(e.target.value)}
                      mb="xs"
                      disabled={loading}
                    />
                    {loadingPantry ? (
                      <Group justify="center" p="xl">
                        <Loader size="sm" />
                      </Group>
                    ) : filteredPantryItems.length === 0 ? (
                      <Text size="sm" c="dimmed" ta="center" py="md">
                        {pantrySearchTerm ? "No items found" : "No pantry items available."}
                      </Text>
                    ) : (
                      <ScrollArea h={200} type="auto">
                        <Table highlightOnHover>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th style={{ width: 40 }}></Table.Th>
                              <Table.Th>Item</Table.Th>
                              <Table.Th>Quantity</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {filteredPantryItems.map((item) => (
                              <Table.Tr
                                key={item.id}
                                onClick={(e) => { e.stopPropagation(); !loading && handleTogglePantryItem(item.id); }}
                                style={{ cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}
                              >
                                <Table.Td>
                                  <Checkbox
                                    checked={selectedPantryIds.has(item.id)}
                                    onChange={() => handleTogglePantryItem(item.id)}
                                    disabled={loading}
                                  />
                                </Table.Td>
                                <Table.Td>{item.displayName}</Table.Td>
                                <Table.Td>
                                  {item.type === "staple" ? "—" : `${item.quantity} ${item.unit}`}
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            </Collapse>
          </Stack>

          {/* Servings + staples note */}
          <Group justify="space-between" align="flex-end">
            <NumberInput
              label="Servings"
              value={servings}
              onChange={(value) => setServings(Number(value) || 1)}
              min={1}
              max={12}
              disabled={loading}
              decimalScale={0}
              allowDecimal={false}
              w={100}
            />
            <Text size="xs" c="dimmed" style={{ flex: 1 }}>
              We'll assume you have common staples like salt, oil, butter, and basic spices
            </Text>
          </Group>

          <Collapse in={showAdvancedOptions}>
            <Textarea
              label="Custom instructions (optional)"
              placeholder='e.g. "make it spicy", "no oven", "vegetarian"'
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              maxLength={500}
              autosize
              minRows={2}
              maxRows={4}
              disabled={loading}
            />
          </Collapse>

          {!showAdvancedOptions && (
            <Group justify="center">
              <Button
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => setShowAdvancedOptions(true)}
                disabled={loading}
              >
                + Add custom instructions
              </Button>
            </Group>
          )}

          {/* Video upload progress */}
          {isUploading && (
            <Stack gap={4}>
              <Text size="sm" c="dimmed">
                Uploading video... {videoUploadProgress}%
              </Text>
              <Progress value={videoUploadProgress} size="sm" animated />
            </Stack>
          )}

          {/* Action Buttons */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose} disabled={loading || isUploading}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              loading={loading || isUploading}
              disabled={
                loading || isUploading || (!hasEnoughInput) || totalSelected > 15
              }
            >
              {isUploading ? 'Uploading video...' : 'Create quick bite'}
            </Button>
          </Group>

          {/* Minimum ingredients hint */}
          {!hasImages && !hasVideo && !hasAudio && totalSelected > 0 && totalSelected < 2 && (
            <Text size="xs" c="dimmed" ta="right">
              Add at least 2 ingredients, or use a photo, video, or voice recording
            </Text>
          )}
        </Stack>
      )}
      </ResponsiveDrawer>
    </>
  );
};
